// services/items.service.ts
import api from "./api.service";

export interface Item {
  itemUuid: string;
  itemName: string;
  itemType: number;
  itemStatus: boolean;
  itemDescription?: string;
  itemLogoUrl?: string;
  ambient: string;
  ambientName?: string;
  storeName?: string;
  companyName?: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  itemInfo?: ItemInfo[];
  purchaseDate: string;
  warrantyUntil: string;
  serialNumber: string;
}

export interface ItemInfo {
  itemInfoUuid: string;
  itemInfoType: number;
  itemInfoName: string;
  itemInfoDescription?: string;
  itemInfoValue: string;
  itemInfoStatus: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationView {
  companyName: string;
  subsidiaries: SubsidiaryView[];
}

export interface SubsidiaryView {
  subsidiaryName: string;
  ambients: AmbientView[];
}

export interface AmbientView {
  ambientName: string;
  ambientUuid: string;
  items: Item[];
}

// Mapear tipos numéricos para texto
export const ITEM_TYPE_MAP: { [key: number]: string } = {
  1: "PDV Web",
  2: "PDV Mobile",
  3: "Impressora",
  4: "Mesa",
  5: "Balcão",
  6: "Equipamento",
  7: "Mobília",
  8: "Climatização",
  9: "Organização",
  10: "Tecnologia",
};

// Configuração de cores para tipos
export const TYPE_COLORS: any = {
  Mobília: { bg: "#e7f3ff", color: "#0066cc", icon: "weekend" },
  Equipamento: { bg: "#d4edda", color: "#155724", icon: "build" },
  "PDV Web": { bg: "#fff3cd", color: "#856404", icon: "point_of_sale" },
  "PDV Mobile": { bg: "#fff3cd", color: "#856404", icon: "smartphone" },
  Climatização: { bg: "#f8d7da", color: "#721c24", icon: "ac_unit" },
  Tecnologia: { bg: "#e9ecef", color: "#495057", icon: "memory" },
  Organização: { bg: "#e7f3ff", color: "#0066cc", icon: "inventory_2" },
  Impressora: { bg: "#e9ecef", color: "#495057", icon: "print" },
  Mesa: { bg: "#e7f3ff", color: "#0066cc", icon: "table_restaurant" },
  Balcão: { bg: "#e7f3ff", color: "#0066cc", icon: "countertops" },
};

class ItemsService {
  // Converter tipo numérico para texto
  getItemTypeText(itemType: number): string {
    return ITEM_TYPE_MAP[itemType] || "Outro";
  }

  // Obter cor do tipo
  getTypeColor(itemType: number) {
    const typeText = this.getItemTypeText(itemType);
    return (
      TYPE_COLORS[typeText] || {
        bg: "#e9ecef",
        color: "#495057",
        icon: "category",
      }
    );
  }

  // Extrair informação específica do item
  getItemInfoValue(
    itemInfo: ItemInfo[] | undefined,
    infoType: number,
    defaultValue: string
  ): string {
    const info = itemInfo?.find((info) => info.itemInfoType === infoType);
    if (!info) return defaultValue;

    try {
      return JSON.parse(info.itemInfoValue).value;
    } catch {
      return info.itemInfoValue;
    }
  }

  // Processar item com informações adicionais
  processItem(item: any, ambient: any, subsidiary: any, company: any): Item {
    return {
      ...item,
      ambientName: ambient.ambientName,
      storeName: subsidiary.subsidiaryName,
      companyName: company.companyName,
      serialNumber: this.getItemInfoValue(
        item.itemInfo,
        3,
        `ITEM-${item.itemUuid.slice(0, 8)}`
      ),
      purchaseDate: this.getItemInfoValue(
        item.itemInfo,
        4,
        new Date(item.createdAt).toISOString().split("T")[0]
      ),
      warrantyUntil: this.getItemInfoValue(
        item.itemInfo,
        5,
        new Date(new Date().setFullYear(new Date().getFullYear() + 1))
          .toISOString()
          .split("T")[0]
      ),
    };
  }

  // Construir visão hierárquica da organização
  buildOrganizationView(organization: any): OrganizationView[] {
    if (!organization?.companies) return [];

    return organization.companies.map((company: any) => ({
      companyName: company.companyName,
      subsidiaries: (company.subsidiaries || []).map((subsidiary: any) => ({
        subsidiaryName: subsidiary.subsidiaryName,
        ambients: (subsidiary.ambients || []).map((ambient: any) => ({
          ambientName: ambient.ambientName,
          ambientUuid: ambient.ambientUuid,
          items: (ambient.items || []).map((item: any) =>
            this.processItem(item, ambient, subsidiary, company)
          ),
        })),
      })),
    }));
  }

  // Extrair todos os itens da organização
  extractAllItems(organization: any): Item[] {
    const items: Item[] = [];

    organization.companies?.forEach((company: any) => {
      company.subsidiaries?.forEach((subsidiary: any) => {
        subsidiary.ambients?.forEach((ambient: any) => {
          ambient.items?.forEach((item: any) => {
            items.push(this.processItem(item, ambient, subsidiary, company));
          });
        });
      });
    });

    return items;
  }

  // Verificar se garantia está válida
  isWarrantyValid(warrantyUntil: string): boolean {
    return new Date(warrantyUntil) > new Date();
  }

  // Obter tipos únicos dos itens
  getUniqueTypes(items: Item[]): string[] {
    return Array.from(
      new Set(items.map((item) => this.getItemTypeText(item.itemType)))
    );
  }

  getUniqueStores(items: Item[]): string[] {
    return Array.from(
      new Set(
        items
          .map((item) => item.storeName)
          .filter((name): name is string => name !== undefined)
      )
    );
  }

  // Filtrar itens
  filterItems(
    items: Item[],
    filters: {
      searchTerm: string;
      statusFilter: string;
      typeFilter: string;
      storeFilter: string;
    }
  ): Item[] {
    return items.filter((item) => {
      const matchesSearch =
        item.itemName
          .toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        item.itemDescription
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        item.serialNumber
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        item.ambientName
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase()) ||
        item.storeName
          ?.toLowerCase()
          .includes(filters.searchTerm.toLowerCase());

      const matchesStatus =
        filters.statusFilter === "all" ||
        (filters.statusFilter === "active" && item.itemStatus) ||
        (filters.statusFilter === "inactive" && !item.itemStatus);

      const matchesType =
        filters.typeFilter === "all" ||
        this.getItemTypeText(item.itemType) === filters.typeFilter;

      const matchesStore =
        filters.storeFilter === "all" || item.storeName === filters.storeFilter;

      return matchesSearch && matchesStatus && matchesType && matchesStore;
    });
  }

  // Deletar item (chamada API)
  async deleteItem(itemUuid: string, token: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/item/${itemUuid}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      return response.ok;
    } catch (error) {
      console.error("Erro ao excluir item:", error);
      return false;
    }
  }

  /**
   * O que está na fila de configuração deste PDV: pendentes agora, pendentes
   * para a abertura do próximo caixa, e os eventos que o worker do OPS RECUSOU.
   *
   * É a única maneira de o usuário descobrir que a configuração dele virou
   * conflito: o PATCH volta 2xx na hora (o evento só foi enfileirado), e quem
   * decide se o PDV aceita é o worker, segundos depois. Sem esta consulta a
   * Central afirmaria "o PDV recebe em instantes" sem ter como saber.
   *
   * Molde: subsidiary.service.ts -> getFilaPorFilial.
   */
  async getFilaPorItem(itemUuid: string) {
    const response = await api.get(`/filaConfig/${itemUuid}`);
    return response.data;
  }

  /**
   * Nome/descrição/tipo do próprio item (ActItem) — não passa pela fila do PDV,
   * porque não é configuração de tela: é o cadastro do item.
   *
   * A resposta traz `infoToken`, e ele NÃO é enfeite. A árvore
   * Empresa → Loja → Ambiente → Itens da Configuração é lida do InfoToken
   * decodificado do store, não de uma chamada HTTP. Sem aplicar o token novo, o
   * usuário salva o nome, reabre o item e vê o nome VELHO — que era exatamente
   * o sintoma do card. Quem chama tem de mandar o `infoToken` para o Zustand.
   *
   * Só estes campos são aceitos pelo backend (lista branca no
   * `updateItemHandler`); mandar campo de tela aqui não derruba mais o update,
   * mas também não grava nada.
   */
  async updateItem(
    itemUuid: string,
    dados: { itemName?: string; itemDescription?: string; itemType?: number }
  ): Promise<Partial<Item> & { infoToken?: string | null }> {
    const response = await api.patch(`/item/${itemUuid}`, dados);
    return response.data;
  }

  /**
   * Cria o item (ActItem). `ambientUuid` é obrigatório e vem do ambiente aberto
   * na tela — o backend recusa com 400 dizendo qual campo falta, e não há
   * ambiente "padrão" que se possa inventar sem pendurar o item na loja errada.
   *
   * Devolve `infoToken` pelo mesmo motivo do `updateItem`.
   */
  async createItem(dados: {
    itemName: string;
    itemType: number;
    ambientUuid: string;
    itemDescription?: string;
  }): Promise<Partial<Item> & { infoToken?: string | null }> {
    const response = await api.post(`/item`, dados);
    return response.data;
  }

  // Calcular estatísticas
  calculateStats(items: Item[]) {
    return {
      total: items.length,
      active: items.filter((item) => item.itemStatus).length,
      types: this.getUniqueTypes(items).length,
      expiredWarranties: items.filter(
        (item) => !this.isWarrantyValid(item.warrantyUntil)
      ).length,
    };
  }
}

export const itemsService = new ItemsService();
export default itemsService;
